import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Camera, User, Check, Loader2, ArrowRight, ArrowLeft, Sparkles,
  RefreshCw, Trash2, Download, FileText, Upload, LogOut, Users, X, Settings as SettingsIcon, AlertCircle, Palette
} from "lucide-react";
import GenreSelector from './GenreSelector';
import Library from './Library';
import SessionToneSelector from './SessionToneSelector';
import "./App.css";

// Code-split heavy routes — these screens are only loaded when their route is visited,
// shrinking the initial JS bundle so the dashboard renders faster on first visit.
const PortraitPlan = lazy(() => import('./PortraitPlan'));
const ImageFit = lazy(() => import('./ImageFit'));
const Settings = lazy(() => import('./Settings'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * BrandLogo — the Curatorial mark. Used wherever the app's identity is
 * shown (header, login/forgot/reset hero, etc.). The static PNG ships from
 * /public/logo.png so it's cached aggressively and renders before any JS.
 * Pass `size` in pixels (defaults to small header size).
 */
const BrandLogo = ({ size = 24, className = "", ...rest }) => (
  <img
    src="/logo.png"
    alt="Curatorial"
    width={size}
    height={size}
    style={{ width: size, height: size }}
    className={`select-none ${className}`}
    draggable={false}
    {...rest}
  />
);


// Auth Context
const AuthContext = React.createContext(null);

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  useEffect(() => {
    // If we just came back from Emergent's Google OAuth flow, the URL hash
    // contains `session_id=...` and <AuthCallback> is about to own the auth
    // handshake (POST /auth/process-session, then setUser). Running checkAuth()
    // here races against process-session: GET /auth/me fires BEFORE the cookie
    // lands, returns 401, and a late setUser(null) can wipe the freshly
    // authenticated user state — making the user think the sign-in failed.
    // Skip the initial check and let AuthCallback resolve the state.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Page
const LoginPage = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);

  // Surface Google OAuth errors that bounced us back from /api/auth/google/callback
  // (?google_error=access_denied, callback_failed, email_not_verified, etc.).
  // Strip the query param after showing the toast so refreshing doesn't re-fire it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('google_error');
    if (err) {
      const friendly = {
        access_denied: "Google sign-in was cancelled.",
        email_not_verified: "Your Google account's email isn't verified — please verify it with Google first.",
        callback_failed: "Google sign-in failed. Please try again, or use email/password.",
      }[err] || `Google sign-in error: ${err}`;
      toast.error(friendly);
      const clean = window.location.pathname;
      window.history.replaceState(null, '', clean);
    }
  }, []);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH. Hit our own backend OAuth start endpoint — it 302s
    // to Google with the configured client_id + redirect_uri, then back to
    // /api/auth/google/callback which sets the session cookie and lands the
    // user on this origin's home page.
    const next = window.location.pathname || "/";
    window.location.href = `${API}/auth/google/login?next=${encodeURIComponent(next)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = mode === 'register' ? '/auth/register' : '/auth/login';
      const response = await axios.post(`${API}${endpoint}`, formData, { withCredentials: true });
      
      setUser(response.data);
      toast.success(mode === 'register' ? 'Account created!' : 'Welcome back!');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="mb-8 text-center">
          <BrandLogo size={88} className="mx-auto mb-6" />
          <h1 className="font-heading text-5xl tracking-tight mb-4">Curatorial</h1>
          <p className="text-muted-foreground">
            Fine art photography inspiration curation
          </p>
        </div>

        {/* Toggle between Login/Register */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setMode('login')}
            className={`flex-1 py-2 px-4 rounded-sm uppercase text-xs tracking-widest ${
              mode === 'login'
                ? 'bg-primary text-primary-foreground'
                : 'bg-transparent border border-border text-muted-foreground'
            }`}
            data-testid="login-tab"
          >
            Sign In
          </button>
          <button
            onClick={() => setMode('register')}
            className={`flex-1 py-2 px-4 rounded-sm uppercase text-xs tracking-widest ${
              mode === 'register'
                ? 'bg-primary text-primary-foreground'
                : 'bg-transparent border border-border text-muted-foreground'
            }`}
            data-testid="register-tab"
          >
            Register
          </button>
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleEmailAuth} className="space-y-4 mb-6">
          {mode === 'register' && (
            <input
              type="text"
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full bg-transparent border border-border focus:border-primary outline-none px-4 py-3 rounded-sm"
              data-testid="name-input"
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className="w-full bg-transparent border border-border focus:border-primary outline-none px-4 py-3 rounded-sm"
            data-testid="email-input"
          />
          <input
            type="password"
            placeholder="Password (min 8 characters)"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
            minLength={8}
            className="w-full bg-transparent border border-border focus:border-primary outline-none px-4 py-3 rounded-sm"
            data-testid="password-input"
          />
          {mode === 'login' && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-xs text-muted-foreground hover:text-primary uppercase tracking-widest"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 py-3 px-8 uppercase tracking-widest text-xs font-bold rounded-sm flex items-center justify-center gap-2"
            data-testid="submit-auth-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {mode === 'register' ? 'Creating Account...' : 'Signing In...'}
              </>
            ) : (
              <>
                {mode === 'register' ? 'Create Account' : 'Sign In'}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or</span>
          </div>
        </div>

        {/* Google Sign In */}
        <button
          onClick={handleGoogleLogin}
          className="w-full bg-transparent border border-border text-foreground hover:bg-muted/20 py-3 px-8 uppercase tracking-widest text-xs font-bold rounded-sm flex items-center justify-center gap-2"
          data-testid="google-login-button"
        >
          <Sparkles className="w-4 h-4" />
          Sign In with Google
        </button>

        <p className="text-xs text-muted-foreground mt-6 text-center">
          Secure authentication with encrypted passwords
        </p>
      </motion.div>
    </div>
  );
};

// Forgot Password Page
const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email }, { withCredentials: true });
      setSubmitted(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
        data-testid="forgot-password-page"
      >
        <div className="mb-8 text-center">
          <BrandLogo size={88} className="mx-auto mb-6" />
          <h1 className="font-heading text-4xl tracking-tight mb-4">Forgot Password</h1>
          <p className="text-muted-foreground text-sm">
            Enter your email and we'll send you a link to reset your password.
          </p>
        </div>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-transparent border border-border focus:border-primary outline-none px-4 py-3 rounded-sm"
              data-testid="forgot-email-input"
            />
            <button
              type="submit"
              disabled={loading || !email}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 py-3 px-8 uppercase tracking-widest text-xs font-bold rounded-sm flex items-center justify-center gap-2"
              data-testid="forgot-submit-button"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Sending...</> : <>Send Reset Link <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full text-xs text-muted-foreground hover:text-primary uppercase tracking-widest py-2"
              data-testid="back-to-login-link"
            >
              ← Back to Sign In
            </button>
          </form>
        ) : (
          <div className="bg-card border border-border rounded-sm p-6 text-center" data-testid="forgot-success">
            <Check className="w-12 h-12 text-success mx-auto mb-4" />
            <h2 className="font-heading text-2xl mb-3">Check your inbox</h2>
            <p className="text-sm text-muted-foreground mb-6">
              If an account exists for <span className="text-foreground font-mono">{email}</span>, a password reset link has been sent. The link expires in 30 minutes.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 px-8 uppercase tracking-widest text-xs font-bold rounded-sm"
              data-testid="forgot-success-back"
            >
              Back to Sign In
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// Reset Password Page (link target from email)
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get('token') || '';

  const [tokenState, setTokenState] = useState('checking'); // 'checking' | 'valid' | 'invalid'
  const [tokenError, setTokenError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setTokenState('invalid');
        setTokenError('No reset token provided.');
        return;
      }
      try {
        const resp = await axios.get(`${API}/auth/verify-reset-token`, { params: { token } });
        setEmail(resp.data?.email || '');
        setTokenState('valid');
      } catch (error) {
        setTokenState('invalid');
        const detail = error.response?.data?.detail;
        setTokenError(typeof detail === 'string' ? detail : 'This reset link is invalid or expired.');
      }
    };
    verify();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
      toast.success('Password updated. Please sign in.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Failed to reset password';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
        data-testid="reset-password-page"
      >
        <div className="mb-8 text-center">
          <BrandLogo size={88} className="mx-auto mb-6" />
          <h1 className="font-heading text-4xl tracking-tight mb-4">Set New Password</h1>
        </div>

        {tokenState === 'checking' && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {tokenState === 'invalid' && (
          <div className="bg-card border border-error/30 rounded-sm p-6 text-center" data-testid="reset-token-invalid">
            <AlertCircle className="w-12 h-12 text-error mx-auto mb-4" />
            <h2 className="font-heading text-xl mb-3">Link no longer valid</h2>
            <p className="text-sm text-muted-foreground mb-6">{tokenError}</p>
            <button
              onClick={() => navigate('/forgot-password')}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 px-8 uppercase tracking-widest text-xs font-bold rounded-sm"
              data-testid="request-new-link"
            >
              Request New Link
            </button>
          </div>
        )}

        {tokenState === 'valid' && !success && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {email && (
              <p className="text-sm text-muted-foreground text-center mb-2">
                Resetting password for <span className="text-foreground font-mono">{email}</span>
              </p>
            )}
            <input
              type="password"
              placeholder="New password (min 8 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full bg-transparent border border-border focus:border-primary outline-none px-4 py-3 rounded-sm"
              data-testid="new-password-input"
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              className="w-full bg-transparent border border-border focus:border-primary outline-none px-4 py-3 rounded-sm"
              data-testid="confirm-password-input"
            />
            <button
              type="submit"
              disabled={loading || password.length < 8 || password !== confirm}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 py-3 px-8 uppercase tracking-widest text-xs font-bold rounded-sm flex items-center justify-center gap-2"
              data-testid="reset-submit-button"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Updating...</> : <>Update Password <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        )}

        {success && (
          <div className="bg-card border border-border rounded-sm p-6 text-center" data-testid="reset-success">
            <Check className="w-12 h-12 text-success mx-auto mb-4" />
            <h2 className="font-heading text-2xl mb-3">Password Updated</h2>
            <p className="text-sm text-muted-foreground mb-6">
              You can now sign in with your new password.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 px-8 uppercase tracking-widest text-xs font-bold rounded-sm"
              data-testid="reset-success-signin"
            >
              Go to Sign In
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// Auth Callback
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    const processAuth = async () => {
      if (hasProcessed.current) return;
      hasProcessed.current = true;

      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.post(
          `${API}/auth/process-session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        // Clear the session_id from the URL so a reload doesn't replay the handshake
        try {
          const cleanUrl = window.location.pathname + window.location.search;
          window.history.replaceState(null, '', cleanUrl);
        } catch (_e) { /* non-critical */ }

        // Verify the cookie actually landed by hitting /auth/me. On some browsers
        // (Safari ITP, cross-site cookie policies) Set-Cookie can fail silently.
        // Up to 3 quick retries with a tiny backoff before falling back to login.
        let verifiedUser = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
            verifiedUser = me.data;
            break;
          } catch (verifyErr) {
            if (attempt === 2) throw verifyErr;
            await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
          }
        }

        // Trust the verified server response over the process-session payload
        setUser(verifiedUser || response.data);
        sessionStorage.setItem('just_authenticated', 'true');
        navigate('/', { replace: true });
      } catch (error) {
        console.error("Auth error:", error);
        const detail = error.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : "Authentication failed — please try again");
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-12 h-12 animate-spin text-primary" />
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkDelay = async () => {
      const justAuth = sessionStorage.getItem('just_authenticated');
      if (!justAuth && !user) {
        await new Promise(r => setTimeout(r, 150));
      } else {
        sessionStorage.removeItem('just_authenticated');
      }
      setChecking(false);
    };

    if (!loading) {
      checkDelay();
    }
  }, [loading, user]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Header Component
const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="border-b border-border bg-background/80 backdrop-blur-sm sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-testid="brand-logo-button">
            <BrandLogo size={28} />
            <span className="font-heading text-xl">Curatorial</span>
          </button>
          
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
            data-testid="settings-link"
          >
            <SettingsIcon className="w-4 h-4" />
            Settings
          </button>

          {/* My Library — only surfaced when the upload feature is unlocked OR
              when the user is an admin (admins should always see it). */}
          {(user?.user_uploads_enabled || user?.plan_type === 'admin') && (
            <button
              onClick={() => navigate('/library')}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
              data-testid="library-link"
            >
              <Upload className="w-4 h-4" />
              My Library
            </button>
          )}
          
          {user?.plan_type === 'admin' && (
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
              data-testid="admin-link"
            >
              <Users className="w-4 h-4" />
              Admin
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm">{user?.name}</p>
            <p className="text-xs text-primary uppercase">{user?.subscription_tier || user?.plan_type}</p>
          </div>
          <img src={user?.picture} alt="Profile" className="w-10 h-10 rounded-full" />
          <button
            onClick={logout}
            className="p-2 hover:bg-muted/20 rounded-sm"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-4xl mx-auto p-8">
        <button
          onClick={() => navigate("/")}
          className="mb-8 flex items-center gap-2 text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="font-heading text-5xl tracking-tight mb-4">Settings</h1>
          <p className="text-muted-foreground">
            Manage your subscription, branding, and storage settings
          </p>
        </div>

        <Settings />
      </div>
    </div>
  );
};

// Admin Panel
const generateTempPassword = () => {
  // 12-char password with mixed case + digits, easy to read
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let pwd = "";
  for (let i = 0; i < 12; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pwd;
};

const AdminPanel = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
    fetchCoverage();
  }, []);

  const fetchCoverage = async () => {
    try {
      const response = await axios.get(`${API}/admin/genre-coverage`, { withCredentials: true });
      setCoverage(response.data?.coverage || []);
    } catch (error) {
      console.error("Coverage fetch error:", error);
    } finally {
      setCoverageLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Fetch users error:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, updates, { withCredentials: true });
      toast.success("User updated successfully");
      fetchUsers();
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const setUserTrial = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/set-trial`, { days: 30 }, { withCredentials: true });
      toast.success("User set to 30-day trial");
      fetchUsers();
    } catch (error) {
      console.error("Set trial error:", error);
      toast.error(error.response?.data?.detail || "Failed to set trial");
    }
  };

  // Toggle the per-user upload feature flag. Idempotent — flips current state.
  const toggleUserUploads = async (u) => {
    const next = !u.user_uploads_enabled;
    try {
      await axios.post(`${API}/admin/users/${u.user_id}/toggle-uploads`, { enabled: next }, { withCredentials: true });
      toast.success(`Uploads ${next ? "enabled" : "disabled"} for ${u.email}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle uploads");
    }
  };

  // Complimentary storage override modal state — admin grants extra GB on top
  // of the user's tier quota. 0 = remove the grant.
  const [storageOverrideUser, setStorageOverrideUser] = useState(null);
  const [storageOverrideGB, setStorageOverrideGB] = useState(0);

  const openStorageOverride = (u) => {
    setStorageOverrideUser(u);
    const currentGB = Math.round(((u.complimentary_storage_bytes || 0) / (1024 ** 3)) * 10) / 10;
    setStorageOverrideGB(currentGB);
  };

  const closeStorageOverride = () => {
    setStorageOverrideUser(null);
    setStorageOverrideGB(0);
  };

  const submitStorageOverride = async () => {
    if (!storageOverrideUser) return;
    try {
      await axios.patch(
        `${API}/admin/users/${storageOverrideUser.user_id}/storage-override`,
        { extra_gb: Number(storageOverrideGB) || 0 },
        { withCredentials: true }
      );
      toast.success(
        Number(storageOverrideGB) > 0
          ? `Granted +${storageOverrideGB} GB to ${storageOverrideUser.email}`
          : `Removed complimentary storage from ${storageOverrideUser.email}`
      );
      closeStorageOverride();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update storage override");
    }
  };

  const openResetPassword = (u) => {
    setResetPwdUser(u);
    setNewPassword(generateTempPassword());
    setResetResult(null);
  };

  const closeResetPassword = () => {
    setResetPwdUser(null);
    setNewPassword("");
    setResetResult(null);
    setResetting(false);
  };

  const submitResetPassword = async () => {
    if (!resetPwdUser) return;
    if (newPassword.trim().length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setResetting(true);
    try {
      const resp = await axios.post(
        `${API}/admin/users/${resetPwdUser.user_id}/set-password`,
        { password: newPassword.trim() },
        { withCredentials: true }
      );
      setResetResult({
        email: resp.data?.email || resetPwdUser.email,
        name: resp.data?.name || resetPwdUser.name,
        password: newPassword.trim(),
      });
      toast.success("Password reset successfully");
    } catch (error) {
      console.error("Reset password error:", error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Failed to reset password";
      toast.error(msg);
    } finally {
      setResetting(false);
    }
  };

  const copyResetResult = async () => {
    if (!resetResult) return;
    const text = `Email: ${resetResult.email}\nTemporary password: ${resetResult.password}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch (e) {
      toast.error("Could not copy — please copy manually");
    }
  };

  if (user?.plan_type !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-error">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto p-8">
        <h1 className="font-heading text-5xl mb-8">User Management</h1>

        {/* Genre Coverage Widget */}
        <div className="mb-10 bg-card border border-border rounded-sm p-6" data-testid="genre-coverage-widget">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading text-2xl mb-1">Genre Coverage</h2>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Portrait plans · Inspiration sessions · Active subscribers (last 30 days)
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  try {
                    toast.info("Refreshing genre inventory…");
                    await axios.post(`${API}/admin/refresh-genre-inventory`, {}, { withCredentials: true });
                    toast.success("Genre inventory refreshed");
                    fetchCoverage();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Refresh failed");
                  }
                }}
                className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary"
                data-testid="refresh-inventory-button"
                title="Re-count S3 images so newly-loaded buckets become selectable"
              >
                Refresh Inventory
              </button>
              <button
                onClick={fetchCoverage}
                className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary"
                data-testid="refresh-coverage-button"
              >
                Refresh
              </button>
            </div>
          </div>

          {coverageLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : !coverage || coverage.length === 0 ? (
            <p className="text-sm text-muted-foreground">No coverage data yet.</p>
          ) : (() => {
            const maxPlans = Math.max(1, ...coverage.map(c => c.portrait_plans));
            const maxSessions = Math.max(1, ...coverage.map(c => c.inspiration_sessions));
            const maxActive = Math.max(1, ...coverage.map(c => c.active_subscribers_30d));
            return (
              <div className="space-y-5">
                {coverage.map((c) => {
                  const folderCounts = c.inspo_counts_by_folder || {};
                  const folderKeys = Object.keys(folderCounts).sort();
                  return (
                  <div key={c.key} data-testid={`coverage-row-${c.key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-lg">{c.name}</span>
                        {!c.available && (
                          <span className="text-[10px] uppercase tracking-widest border border-warning/40 text-warning px-2 py-0.5 rounded-sm">
                            locked
                          </span>
                        )}
                        {c.inspo_image_count > 0 && (
                          <span className="text-[10px] uppercase tracking-widest text-muted-foreground" data-testid={`coverage-${c.key}-inspo-total`}>
                            · {c.inspo_image_count} curated
                          </span>
                        )}
                      </div>
                      {folderKeys.length > 0 && (
                        <div className="flex items-center gap-3 text-[11px] text-muted-foreground" data-testid={`coverage-${c.key}-folder-breakdown`}>
                          {folderKeys.map((fk) => (
                            <span key={fk} className="font-mono" data-testid={`coverage-${c.key}-folder-${fk}`}>
                              <span className="uppercase tracking-widest text-[10px] mr-1">{fk}</span>
                              <span className="text-primary">{folderCounts[fk]}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Portrait Plans</span>
                          <span className="text-primary font-mono" data-testid={`coverage-${c.key}-plans`}>{c.portrait_plans}</span>
                        </div>
                        <div className="h-2 bg-muted/30 rounded-sm overflow-hidden">
                          <div
                            className="h-full bg-primary"
                            style={{ width: `${(c.portrait_plans / maxPlans) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Inspiration Sessions</span>
                          <span className="text-primary font-mono" data-testid={`coverage-${c.key}-sessions`}>{c.inspiration_sessions}</span>
                        </div>
                        <div className="h-2 bg-muted/30 rounded-sm overflow-hidden">
                          <div
                            className="h-full bg-success"
                            style={{ width: `${(c.inspiration_sessions / maxSessions) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Active (30d)</span>
                          <span className="text-primary font-mono" data-testid={`coverage-${c.key}-active`}>{c.active_subscribers_30d}</span>
                        </div>
                        <div className="h-2 bg-muted/30 rounded-sm overflow-hidden">
                          <div
                            className="h-full bg-blue-400"
                            style={{ width: `${(c.active_subscribers_30d / maxActive) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {loading ? (
          <div className="flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/20">
                <tr>
                  <th className="text-left p-4 text-sm uppercase tracking-widest">User</th>
                  <th className="text-left p-4 text-sm uppercase tracking-widest">Email</th>
                  <th className="text-left p-4 text-sm uppercase tracking-widest">Subscription</th>
                  <th className="text-left p-4 text-sm uppercase tracking-widest">Account</th>
                  <th className="text-left p-4 text-sm uppercase tracking-widest">Actions</th>
                  <th className="text-left p-4 text-sm uppercase tracking-widest">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-border">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                        <span>{u.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">{u.email}</td>
                    <td className="p-4">
                      <select
                        value={u.subscription_tier || 'pending'}
                        onChange={(e) => updateUser(u.user_id, { subscription_tier: e.target.value, account_status: e.target.value === 'pending' ? 'pending' : 'approved' })}
                        className="bg-transparent border border-border rounded-sm px-3 py-1 text-sm"
                      >
                        <option value="pending">Pending</option>
                        <option value="trial">Trial</option>
                        <option value="solo">Solo ($24/mo)</option>
                        <option value="creator">Creator ($49/mo)</option>
                        <option value="pro_mentor">Pro Mentor ($129/mo)</option>
                      </select>
                    </td>
                    <td className="p-4">
                      {u.account_status === 'pending' ? (
                        <span className="px-3 py-1 rounded-sm text-xs bg-warning/10 text-warning border border-warning/30">
                          Pending
                        </span>
                      ) : u.subscription_tier === 'trial' ? (
                        <span className="px-3 py-1 rounded-sm text-xs bg-blue-500/10 text-blue-500 border border-blue-500/30">
                          Trial
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-sm text-xs bg-success/10 text-success">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        {u.account_status === 'pending' && (
                          <button
                            onClick={() => setUserTrial(u.user_id)}
                            className="px-3 py-1 rounded-sm text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                          >
                            Start 30-day Trial
                          </button>
                        )}
                        <button
                          onClick={() => updateUser(u.user_id, { is_active: !u.is_active })}
                          className={`px-3 py-1 rounded-sm text-xs ${
                            u.is_active
                              ? 'bg-error/10 text-error hover:bg-error/20'
                              : 'bg-success/10 text-success hover:bg-success/20'
                          }`}
                        >
                          {u.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => openResetPassword(u)}
                          data-testid={`reset-password-btn-${u.user_id}`}
                          className="px-3 py-1 rounded-sm text-xs bg-muted/30 text-foreground hover:bg-muted/50 border border-border"
                        >
                          Reset Password
                        </button>
                        <button
                          onClick={() => toggleUserUploads(u)}
                          data-testid={`toggle-uploads-btn-${u.user_id}`}
                          className={`px-3 py-1 rounded-sm text-xs border ${
                            u.user_uploads_enabled
                              ? 'border-success text-success hover:bg-success/10'
                              : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                          }`}
                          title={u.user_uploads_enabled ? 'Revoke complimentary upload access' : 'Grant complimentary upload access'}
                        >
                          {u.user_uploads_enabled ? 'Upload Access: ON' : 'Upload Access: OFF'}
                        </button>
                        <button
                          onClick={() => openStorageOverride(u)}
                          data-testid={`storage-override-btn-${u.user_id}`}
                          className="px-3 py-1 rounded-sm text-xs border border-border text-muted-foreground hover:border-primary hover:text-primary"
                          title="Grant complimentary storage on top of tier quota"
                        >
                          {`+${Number(((u.complimentary_storage_bytes || 0) / (1024 ** 3))).toFixed(0)} GB`}
                        </button>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reset Password Modal */}
      {resetPwdUser && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="reset-password-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closeResetPassword(); }}
        >
          <div className="bg-card border border-border rounded-sm max-w-md w-full p-6">
            <h2 className="font-heading text-2xl mb-2">Reset Password</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {resetPwdUser.name} <span className="text-foreground/60">({resetPwdUser.email})</span>
            </p>

            {!resetResult ? (
              <>
                <label className="block text-xs uppercase tracking-widest mb-2 text-muted-foreground">
                  New temporary password
                </label>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="reset-password-input"
                    className="flex-1 bg-transparent border border-border rounded-sm px-3 py-2 text-sm font-mono"
                    placeholder="At least 8 characters"
                  />
                  <button
                    onClick={() => setNewPassword(generateTempPassword())}
                    type="button"
                    className="px-3 py-2 rounded-sm text-xs bg-muted/30 hover:bg-muted/50 border border-border"
                    data-testid="reset-password-generate"
                  >
                    Generate
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-6">
                  This will replace the user's current password and sign them out of all sessions. Share the new password with them via a secure channel.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={closeResetPassword}
                    className="px-4 py-2 rounded-sm text-sm border border-border hover:bg-muted/20"
                    data-testid="reset-password-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitResetPassword}
                    disabled={resetting || newPassword.trim().length < 8}
                    className="px-4 py-2 rounded-sm text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    data-testid="reset-password-submit"
                  >
                    {resetting ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-success/10 border border-success/30 rounded-sm p-4 mb-4">
                  <p className="text-success text-sm mb-3">Password reset successfully.</p>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-muted-foreground">Email:</span> <span className="font-mono">{resetResult.email}</span></div>
                    <div><span className="text-muted-foreground">Temp password:</span> <span className="font-mono" data-testid="reset-password-result">{resetResult.password}</span></div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mb-6">
                  Copy this and share it with the user securely. They should change it after logging in.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={copyResetResult}
                    className="px-4 py-2 rounded-sm text-sm bg-muted/30 hover:bg-muted/50 border border-border"
                    data-testid="reset-password-copy"
                  >
                    Copy
                  </button>
                  <button
                    onClick={closeResetPassword}
                    className="px-4 py-2 rounded-sm text-sm bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="reset-password-done"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Complimentary Storage Override Modal */}
      {storageOverrideUser && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="storage-override-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closeStorageOverride(); }}
        >
          <div className="bg-card border border-border rounded-sm max-w-md w-full p-6">
            <h2 className="font-heading text-2xl mb-2">Complimentary storage</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {storageOverrideUser.name}
              <span className="text-foreground/60"> ({storageOverrideUser.email})</span>
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Adds the amount below ON TOP of the user's current tier quota.
              Set to 0 to remove the grant. Useful for beta testers, grandfathered
              accounts, or special-case business arrangements.
            </p>
            <label className="block text-xs uppercase tracking-widest mb-2 text-muted-foreground">
              Extra storage (GB)
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={storageOverrideGB}
              onChange={(e) => setStorageOverrideGB(e.target.value)}
              data-testid="storage-override-input"
              className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm font-mono mb-6"
              placeholder="0"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={closeStorageOverride}
                className="px-4 py-2 rounded-sm text-sm border border-border hover:bg-muted/20"
                data-testid="storage-override-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitStorageOverride}
                className="px-4 py-2 rounded-sm text-sm bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="storage-override-submit"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [s3Status, setS3Status] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);

  useEffect(() => {
    if (user) {
      fetchS3Status();
      fetchRecentSessions();
    }
  }, [user]);

  const fetchS3Status = async () => {
    try {
      const response = await axios.get(`${API}/s3-status`, { withCredentials: true });
      setS3Status(response.data);
    } catch (error) {
      console.error("S3 Status Error:", error);
      toast.error("Failed to connect to S3");
    }
  };

  const fetchRecentSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions`, { withCredentials: true });
      setRecentSessions(response.data.slice(0, 5));
    } catch (error) {
      console.error("Sessions Error:", error);
    }
  };

  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation();
    
    if (!window.confirm("Delete this session? This cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/sessions/${sessionId}`, { withCredentials: true });
      toast.success("Session deleted");
      fetchRecentSessions();
    } catch (error) {
      console.error("Delete Error:", error);
      toast.error("Failed to delete session");
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Pending Status Banner */}
      {user?.account_status === 'pending' && (
        <div className="bg-warning/10 border-b border-warning/30 p-4">
          <div className="max-w-7xl mx-auto flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-warning font-medium">Account Pending Approval</p>
              <p className="text-sm text-warning/80 mt-1">
                Your account is awaiting activation. You can browse themes and create profiles, but analysis features are not available until your account is approved. Please contact support if you have questions.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="p-8"
        data-testid="dashboard"
      >
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h1 className="font-heading text-6xl lg:text-7xl tracking-tight mb-4">Curatorial Dashboard</h1>
            <p className="text-muted-foreground text-lg">Curate inspiration with precision and intention</p>
          </div>

          {/* Header Info Section - S3 Status & Plan Usage */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <motion.div
              className="bg-card border border-border p-6 rounded-sm"
              data-testid="s3-status-card"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-success/10 rounded-sm flex items-center justify-center">
                  <Camera className="w-6 h-6 text-success" />
                </div>
                <div>
                  <h3 className="font-heading text-lg mb-1">Inspiration Deck</h3>
                  {s3Status ? (
                    <>
                      <p className="text-success text-sm">Connected • {s3Status.image_count || 0} images</p>
                    </>
                  ) : (
                    <p className="text-muted-foreground text-sm">Checking connection...</p>
                  )}
                </div>
              </div>
            </motion.div>

            {user?.usage && (
              <motion.div
                className="bg-card border border-border p-6 rounded-sm"
                data-testid="usage-stats-card"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                    user.usage.is_at_limit ? 'bg-error/10' : 'bg-primary/10'
                  }`}>
                    <User className={`w-6 h-6 ${user.usage.is_at_limit ? 'text-error' : 'text-primary'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg mb-1">Plan Usage</h3>
                    <p className="text-sm">
                      <span className={user.usage.is_at_limit ? 'text-error font-bold' : 'text-foreground font-bold'}>
                        {user.usage.current_usage} / {user.usage.limit}
                      </span>
                      <span className="text-muted-foreground"> sessions • {user.subscription_tier || user.plan_type}</span>
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Main Action Tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* 1. Portrait Plan */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/portrait-plan')}
              className="bg-card border border-primary/30 p-8 rounded-sm cursor-pointer"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center mb-4">
                <Palette className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading text-2xl mb-2">Portrait Plan</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Create personalized portrait plans with wardrobe recommendations
              </p>
              <div className="flex items-center gap-2 text-primary text-sm">
                <span>Get Started</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </motion.div>

            {/* 2. Inspiration (renamed from New Session) */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-card border border-border hover:border-primary/30 p-8 rounded-sm cursor-pointer"
              onClick={() => navigate("/client-profile")}
              data-testid="new-session-card"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading text-2xl mb-2">Inspiration</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Curate themed inspiration for your next portrait session
              </p>
              <div className="flex items-center gap-2 text-primary text-sm">
                <span>Start Curating</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </motion.div>

            {/* 3. Image Fit (new) */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/image-fit')}
              className="bg-card border border-border hover:border-primary/30 p-8 rounded-sm cursor-pointer"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center mb-4">
                <Camera className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading text-2xl mb-2">Image Fit</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Analyze session images with AI-generated client descriptions
              </p>
              <div className="flex items-center gap-2 text-primary text-sm">
                <span>Analyze Images</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Client Profile (Phase 3: lean genre-aware form)
const ClientProfile = () => {
  const navigate = useNavigate();
  const [genre, setGenre] = useState(null);
  const [formData, setFormData] = useState({
    client_name: "",
    personality_type: "introvert",
    comfort_level: 3,
    emotional_tone: ""
  });
  // Phase 3 lean selectors
  const [personalityEnergy, setPersonalityEnergy] = useState('');
  const [personalityPresence, setPersonalityPresence] = useState('');
  const [emotionalCore, setEmotionalCore] = useState('');
  const [sessionTone, setSessionTone] = useState('');
  const [sessionToneCustom, setSessionToneCustom] = useState('');
  const [gender, setGender] = useState('');  // boy | girl (only when genre.form_options.show_gender)
  // User-upload feature: ratio of personal images to mix with curated (0/25/50/75/100).
  // null = not yet loaded; otherwise the effective default from preferences.
  const [uploadFeatureEnabled, setUploadFeatureEnabled] = useState(false);
  const [userUploadRatio, setUserUploadRatio] = useState(0);
  const [userImageCount, setUserImageCount] = useState(0);
  const [genrePack, setGenrePack] = useState(null);
  const [fixedOptions, setFixedOptions] = useState(null);
  const [genreLoading, setGenreLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [recentSessions, setRecentSessions] = useState([]);

  useEffect(() => {
    fetchRecentSessions();
  }, []);

  // Load fixed options + full pack whenever a genre is selected
  useEffect(() => {
    if (!genre) {
      setGenrePack(null);
      setFixedOptions(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setGenreLoading(true);
      try {
        // Fetch the pack + canonical genre list + upload-feature status + user's
        // default ratio preference in parallel. Genre list also carries the user
        // image count per genre when uploads are enabled.
        const [packResp, listResp, featureResp, prefsResp] = await Promise.all([
          axios.get(`${API}/genres/${genre}`, { withCredentials: true }),
          axios.get(`${API}/genres`, { withCredentials: true }),
          axios.get(`${API}/uploads/feature-status`, { withCredentials: true }).catch(() => ({ data: { enabled: false } })),
          axios.get(`${API}/user/preferences`, { withCredentials: true }).catch(() => ({ data: { user_upload_ratio_default: 0 } })),
        ]);
        if (cancelled) return;
        setGenrePack(packResp.data);
        setFixedOptions({
          personality: listResp.data?.personality || { energy: [], presence: [] },
          emotional_core_options: listResp.data?.emotional_core_options || [],
        });
        // Upload feature + per-genre user image count
        const featureOn = !!featureResp.data?.enabled;
        setUploadFeatureEnabled(featureOn);
        const myGenre = (listResp.data?.genres || []).find((g) => g.key === genre);
        setUserImageCount((myGenre?.user_image_count) || 0);
        // Seed ratio with the preference default; 0 if uploads off
        const defaultRatio = Number(prefsResp.data?.user_upload_ratio_default) || 0;
        setUserUploadRatio(featureOn ? defaultRatio : 0);
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : detail?.message || "Could not load genre pack";
        toast.error(msg);
      } finally {
        if (!cancelled) setGenreLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [genre]);

  const fetchRecentSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions`, { withCredentials: true });
      setRecentSessions(response.data.slice(0, 10));
    } catch (error) {
      console.error("Sessions Error:", error);
    }
  };

  // Auto-refresh Recent Sessions every 5s while any session is still analyzing
  useEffect(() => {
    const anyAnalyzing = recentSessions.some((s) => s.status === 'analyzing');
    if (!anyAnalyzing) return;
    const tid = setInterval(fetchRecentSessions, 5000);
    return () => clearInterval(tid);
  }, [recentSessions]);

  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation();
    
    if (!window.confirm("Delete this session? This cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/sessions/${sessionId}`, { withCredentials: true });
      toast.success("Session deleted");
      fetchRecentSessions();
    } catch (error) {
      console.error("Delete Error:", error);
      toast.error("Failed to delete session");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Personality is only required for genres that show it (e.g. Women's Beauty).
    // Family hides it entirely — uses identity_expressions/recognition_goals instead.
    const showPersonality = genrePack?.form_options?.show_personality !== false;
    const showGender = !!genrePack?.form_options?.show_gender;
    if (showPersonality && (!personalityEnergy || !personalityPresence)) {
      toast.error("Please select both Personality Energy and Presence");
      return;
    }
    if (showGender && !gender) {
      toast.error("Please select Boy or Girl");
      return;
    }
    if (!emotionalCore) {
      toast.error("Please select an Emotional Core");
      return;
    }
    setLoading(true);

    try {
      const payload = {
        client_name: formData.client_name,
        emotional_tone: formData.emotional_tone || "",
        genre,
        emotional_core: emotionalCore,
      };
      // Personality fields are only sent when the genre uses them.
      if (showPersonality) {
        payload.personality_energy = personalityEnergy;
        payload.personality_presence = personalityPresence;
      }
      if (showGender) {
        payload.gender = gender;
      }
      // Include user_upload_ratio whenever the feature is unlocked AND the user
      // actually has uploads for this genre. Backend will fall back to the
      // preference default if we don't pass anything.
      if (uploadFeatureEnabled && userImageCount > 0) {
        payload.user_upload_ratio = userUploadRatio;
      }
      // Comfort level only for genres that use it
      if (genrePack?.uses_comfort_level) {
        payload.comfort_level = formData.comfort_level;
      }
      // Session tone — only for genres that define tone_options
      if (sessionTone) {
        payload.session_tone = sessionTone;
        if (sessionTone === 'custom' && sessionToneCustom.trim()) {
          payload.session_tone_custom = sessionToneCustom.trim();
        }
      }
      const response = await axios.post(`${API}/client-profile`, payload, { withCredentials: true });
      toast.success("Client profile created!");
      navigate("/theme-selection", {
        state: {
          profileId: response.data.id,
          genre,
          genrePack,
        },
      });
    } catch (error) {
      console.error("Error creating profile:", error);
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (typeof detail === 'object' && detail.message) {
          toast.error(detail.message);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error("Feature not available for your subscription tier");
        }
      } else {
        toast.error("Failed to create profile");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error("Please upload a PDF file");
      return;
    }

    setUploadedFile(file);
    setExtracting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/extract-portrait-plan`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      const extracted = response.data;
      
      setFormData({
        client_name: extracted.client_name || "",
        personality_type: extracted.personality_type || "introvert",
        comfort_level: extracted.comfort_level || 3,
        emotional_tone: extracted.emotional_tone || ""
      });

      if (extracted.themes && extracted.themes.length > 0) {
        sessionStorage.setItem('extracted_themes', JSON.stringify(extracted.themes));
        toast.success(`Portrait plan extracted! Found ${extracted.themes.length} theme(s): ${extracted.themes.join(', ')}`);
      } else {
        toast.success("Portrait plan extracted!");
      }
    } catch (error) {
      console.error("Extraction error:", error);
      toast.error("Failed to extract data from PDF");
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        className="p-8"
        data-testid="client-profile-page"
      >
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate("/")}
            className="mb-8 flex items-center gap-2 text-muted-foreground hover:text-primary"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>

          {!genre ? (
            <div className="bg-card border border-border p-8 rounded-sm">
              <GenreSelector
                value={genre}
                onSelect={(g) => setGenre(g)}
                title="Inspiration · Select Genre"
                subtitle="Choose the genre for this inspiration session. Available options and image sources will load from the matching Genre Pack."
              />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-end mb-4">
                <button
                  type="button"
                  onClick={() => setGenre(null)}
                  className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary"
                  data-testid="change-genre-inspiration"
                >
                  Genre: <span className="text-primary">{({womens_beauty:"Women's Beauty",family:"Family",child:"Child",senior:"Senior",branding:"Branding"})[genre] || genre}</span> · Change
                </button>
              </div>

              <div className="mb-12">
                <h1 className="font-heading text-5xl lg:text-6xl tracking-tight mb-4">Client Persona</h1>
                <p className="text-muted-foreground text-lg">Define the essence of your subject</p>
              </div>

          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              type="button"
              onClick={() => setUploadMode(false)}
              className={`px-6 py-2 rounded-sm uppercase text-xs tracking-widest ${
                !uploadMode ? "bg-primary text-primary-foreground" : "bg-transparent border border-border text-muted-foreground"
              }`}
              data-testid="manual-mode-button"
            >
              Manual Entry
            </button>
            <button
              type="button"
              onClick={() => setUploadMode(true)}
              className={`flex items-center gap-2 px-6 py-2 rounded-sm uppercase text-xs tracking-widest ${
                uploadMode ? "bg-primary text-primary-foreground" : "bg-transparent border border-border text-muted-foreground"
              }`}
              data-testid="upload-mode-button"
            >
              <Upload className="w-4 h-4" />
              Upload Portrait Plan
            </button>
          </div>

          {uploadMode && (
            <div className="mb-8 p-8 border-2 border-dashed border-border rounded-sm text-center">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
                id="pdf-upload"
                data-testid="pdf-upload-input"
              />
              <label htmlFor="pdf-upload" className="cursor-pointer flex flex-col items-center gap-4">
                <Upload className="w-12 h-12 text-primary" />
                <div>
                  <p className="text-lg mb-2">Upload Portrait Plan PDF</p>
                  <p className="text-sm text-muted-foreground">AI will extract client name, personality, comfort level, and themes</p>
                </div>
                {uploadedFile && <p className="text-sm text-primary">{uploadedFile.name}</p>}
                {extracting && (
                  <div className="flex items-center gap-2 text-primary">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Extracting data...
                  </div>
                )}
              </label>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-3">Client Name</label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                required
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none px-0 py-3 text-lg"
                placeholder="Enter name"
                data-testid="client-name-input"
              />
            </div>

            {genrePack?.form_options?.show_personality !== false && (
              <>
                <div>
                  <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-4">Personality · Energy</label>
                  {genreLoading || !fixedOptions ? (
                    <div className="text-xs text-muted-foreground">Loading options…</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {(fixedOptions.personality?.energy || []).map((opt) => {
                        const sel = personalityEnergy === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setPersonalityEnergy(opt.value)}
                            className={`text-left p-4 border rounded-sm transition ${
                              sel ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/30"
                            }`}
                            data-testid={`inspiration-energy-${opt.value}`}
                          >
                            <div className="text-sm uppercase tracking-widest mb-1">{opt.label}</div>
                            <div className="text-xs text-muted-foreground leading-snug">{opt.meaning}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-4">Personality · Presence</label>
                  {genreLoading || !fixedOptions ? (
                    <div className="text-xs text-muted-foreground">Loading options…</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {(fixedOptions.personality?.presence || []).map((opt) => {
                        const sel = personalityPresence === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setPersonalityPresence(opt.value)}
                            className={`p-4 border rounded-sm uppercase text-xs tracking-widest transition ${
                              sel ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/30"
                            }`}
                            data-testid={`inspiration-presence-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {genrePack?.form_options?.show_gender && (
              <div>
                <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-4">Subject</label>
                <div className="grid grid-cols-2 gap-3 max-w-md">
                  {((genrePack?.gender_folders?.options) || [
                    { value: 'boy', label: 'Boy' },
                    { value: 'girl', label: 'Girl' },
                  ]).map((opt) => {
                    const sel = gender === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setGender(opt.value)}
                        className={`p-4 border rounded-sm uppercase text-xs tracking-widest transition ${
                          sel ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/30"
                        }`}
                        data-testid={`inspiration-gender-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-4">Emotional Core</label>
              <p className="text-xs text-muted-foreground mb-3">The inner feeling state the subject most wishes to inhabit. This biases the AI image search.</p>
              {genreLoading || !fixedOptions ? (
                <div className="text-xs text-muted-foreground">Loading options…</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(fixedOptions.emotional_core_options || []).map((opt) => {
                    const sel = emotionalCore === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setEmotionalCore(opt)}
                        className={`p-4 border rounded-sm text-sm transition ${
                          sel ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/30"
                        }`}
                        data-testid={`inspiration-emotional-core-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {genrePack?.uses_comfort_level && (
              <div>
                <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-4">
                  Comfort Level: {formData.comfort_level}
                </label>
                <p className="text-xs text-muted-foreground mb-4">1 = Extremely Conservative | 6 = Extremely Free-Spirited</p>
                <input
                  type="range"
                  min="1"
                  max="6"
                  value={formData.comfort_level}
                  onChange={(e) => setFormData({...formData, comfort_level: parseInt(e.target.value)})}
                  className="w-full h-1 bg-border rounded-sm appearance-none cursor-pointer accent-primary"
                  data-testid="comfort-level-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span><span>6</span>
                </div>
              </div>
            )}

            <SessionToneSelector
              genrePack={genrePack}
              value={sessionTone}
              onChange={(k) => setSessionTone(k)}
              customValue={sessionToneCustom}
              onCustomChange={setSessionToneCustom}
            />

            <div>
              <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-3">Emotional Tone (Optional)</label>
              <textarea
                value={formData.emotional_tone}
                onChange={(e) => setFormData({...formData, emotional_tone: e.target.value})}
                className="w-full bg-transparent border border-border focus:border-primary outline-none p-4 text-sm rounded-sm"
                placeholder="Describe the emotional essence, themes, or keywords..."
                rows="4"
                data-testid="emotional-tone-input"
              />
            </div>

            {/* Personal image mix — only shown when the user has the upload feature
                unlocked AND has personal images uploaded for this genre. */}
            {uploadFeatureEnabled && userImageCount > 0 && (
              <div data-testid="user-upload-ratio-block">
                <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-3">
                  Image Mix
                  <span className="ml-2 normal-case tracking-normal text-muted-foreground/70">
                    · You have {userImageCount} personal image{userImageCount === 1 ? "" : "s"} for this genre
                  </span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {[0, 25, 50, 75, 100].map((r) => {
                    const sel = userUploadRatio === r;
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setUserUploadRatio(r)}
                        className={`px-4 py-2 rounded-sm text-xs uppercase tracking-widest border transition ${
                          sel ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"
                        }`}
                        data-testid={`user-upload-ratio-${r}`}
                      >
                        {r === 0 ? "Curated only" : `${r}% mine`}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  How much of this session should pull from <em>your</em> uploaded images versus the curated library.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-4 px-8 uppercase tracking-widest text-xs font-bold rounded-sm flex items-center justify-center gap-2"
              data-testid="submit-profile-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating Profile...
                </>
              ) : (
                <>
                  Continue to Themes
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Recent Sessions */}
          {recentSessions.length > 0 && (
            <div className="mt-12 bg-card border border-border p-8 rounded-sm">
              <h3 className="font-heading text-2xl mb-6">Recent Sessions</h3>
              <div className="space-y-4">
                {recentSessions.map((session) => {
                  const p = session.progress;
                  const isAnalyzing = session.status === 'analyzing';
                  const progressPct = isAnalyzing && p?.themes_total
                    ? Math.round((((p.themes_done || 0) * (p.images_total || 1)) + (p.images_done || 0)) / ((p.themes_total || 1) * (p.images_total || 1)) * 100)
                    : 0;
                  return (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-4 bg-muted/20 border border-border rounded-sm hover:border-primary/30 cursor-pointer group"
                    onClick={() => navigate(`/results/${session.id}`)}
                    data-testid={`session-${session.id}`}
                  >
                    <div className="flex-1">
                      <p className="font-heading text-lg mb-1">{session.client_name || "Unknown Client"}</p>
                      <p className="font-mono text-xs text-primary mb-1">{session.selected_themes.join(", ")}</p>
                      <p className="text-xs text-muted-foreground">{new Date(session.created_at).toLocaleDateString()}</p>
                      {isAnalyzing && p && (
                        <div className="mt-2" data-testid={`session-progress-${session.id}`}>
                          <p className="text-xs text-warning mb-1">
                            Analyzing <span className="text-primary">{p.current_theme || '—'}</span> · theme {Math.min((p.themes_done || 0) + 1, p.themes_total || 1)}/{p.themes_total || 1} · images {p.images_done || 0}/{p.images_total || 0}
                          </p>
                          <div className="h-1 bg-muted/40 rounded-sm overflow-hidden max-w-xs">
                            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progressPct}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-3 py-1 rounded-sm ${
                        session.status === "completed" ? "bg-success/10 text-success" :
                        session.status === "failed" ? "bg-error/10 text-error" :
                        isAnalyzing ? "bg-primary/10 text-primary animate-pulse" :
                        "bg-warning/10 text-warning"
                      }`}>
                        {session.status}
                      </span>
                      <button
                        onClick={(e) => handleDeleteSession(session.id, e)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-error/10 rounded-sm"
                        data-testid={`delete-session-${session.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-error" />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Theme Selection (keep existing, add Header)
const ThemeSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const profileId = location.state?.profileId;
  const sessionData = location.state?.sessionData;
  const initialGenre = location.state?.genre || sessionData?.genre || null;
  const initialGenrePack = location.state?.genrePack || null;
  
  const extractedThemes = sessionStorage.getItem('extracted_themes');
  const initialThemes = sessionData?.themes || (extractedThemes ? JSON.parse(extractedThemes) : []);
  
  const [selectedThemes, setSelectedThemes] = useState(initialThemes);
  const [imagesPerTheme, setImagesPerTheme] = useState(sessionData?.imagesPerTheme || 5);
  const [loading, setLoading] = useState(false);
  const [clientProfile, setClientProfile] = useState(sessionData?.clientProfile || null);
  const [genrePack, setGenrePack] = useState(initialGenrePack);
  const [packLoading, setPackLoading] = useState(false);
  const [progress, setProgress] = useState(null);

  const isPending = user?.account_status === 'pending';

  useEffect(() => {
    if (extractedThemes) {
      sessionStorage.removeItem('extracted_themes');
    }
  }, []);

  // Phase 3: ensure we always have the genre pack (when navigating back from results, location.state may be empty)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (genrePack) return;
      let genre = initialGenre;
      // If still no genre and we have a profileId, fetch the profile to find its genre
      if (!genre && profileId) {
        try {
          const resp = await axios.get(`${API}/client-profiles`, { withCredentials: true });
          const cp = (resp.data || []).find(p => p.id === profileId);
          if (cp) {
            genre = cp.genre || 'womens_beauty';
            if (!clientProfile) setClientProfile(cp);
          }
        } catch (e) {
          // best-effort
        }
      }
      genre = genre || 'womens_beauty';
      try {
        setPackLoading(true);
        const r = await axios.get(`${API}/genres/${genre}`, { withCredentials: true });
        if (!cancelled) setGenrePack(r.data);
      } catch (e) {
        if (!cancelled) toast.error("Could not load Recognition Goals");
      } finally {
        if (!cancelled) setPackLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [profileId, initialGenre, genrePack]);

  const toggleTheme = (theme) => {
    if (selectedThemes.includes(theme)) {
      setSelectedThemes(selectedThemes.filter(t => t !== theme));
    } else if (selectedThemes.length < 3) {
      setSelectedThemes([...selectedThemes, theme]);
    } else {
      toast.warning("Maximum 3 recognition goals allowed");
    }
  };

  const handleAnalyze = async () => {
    if (selectedThemes.length < 1 || selectedThemes.length > 3) {
      toast.error("Please select 1-3 themes");
      return;
    }

    setLoading(true);

    try {
      const sessionResponse = await axios.post(`${API}/sessions`, {
        session_id: profileId,
        themes: selectedThemes,
        images_per_theme: imagesPerTheme
      }, { withCredentials: true });

      const sessionId = sessionResponse.data.id;
      toast.success("Analysis started — this can take a few minutes…");

      // Kick off the (now asynchronous) backend analysis. Returns immediately with status="started".
      await axios.post(`${API}/analyze?session_id=${sessionId}`, {}, { withCredentials: true });

      // Poll session status until completed/failed (max 12 minutes)
      const startedAt = Date.now();
      const maxMs = 12 * 60 * 1000;
      while (Date.now() - startedAt < maxMs) {
        await new Promise((r) => setTimeout(r, 4000));
        let statusResp;
        try {
          statusResp = await axios.get(`${API}/sessions/${sessionId}`, { withCredentials: true });
        } catch (pollErr) {
          // Transient network error — keep polling
          console.warn("Status poll failed, retrying…", pollErr?.message);
          continue;
        }
        const status = statusResp.data?.status;
        // Surface progress for the loading UI
        setProgress(statusResp.data?.progress || null);
        if (status === "completed") {
          toast.success("Analysis complete!");
          setProgress(null);
          navigate(`/results/${sessionId}`);
          return;
        }
        if (status === "failed") {
          toast.error(statusResp.data?.error || "Analysis failed");
          setProgress(null);
          setLoading(false);
          return;
        }
        // status is still "analyzing" or "in_progress" — keep polling
      }

      toast.warning("Analysis is taking longer than expected. Check Recent Sessions in a few minutes.");
      setProgress(null);
      navigate("/");
    } catch (error) {
      console.error("Analysis Error:", error);
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (typeof detail === 'object' && detail.message) {
          // Handle subscription limit error
          toast.error(detail.message);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error("Feature not available for your subscription tier");
        }
      } else {
        toast.error("Failed to analyze images");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        className="p-8"
        data-testid="theme-selection-page"
      >
        <div className="max-w-7xl mx-auto">
          <button onClick={() => navigate(-1)} className="mb-8 flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="mb-12">
            <h1 className="font-heading text-5xl lg:text-6xl tracking-tight mb-4">Recognition Goals</h1>
            {clientProfile && (
              <div className="mb-4">
                <p className="text-primary text-lg">{clientProfile.client_name}</p>
                <p className="text-muted-foreground text-sm">
                  {clientProfile.personality_type || '—'}{genrePack?.uses_comfort_level && clientProfile.comfort_level ? ` • Comfort Level ${clientProfile.comfort_level}/6` : ''}
                </p>
              </div>
            )}
            <p className="text-muted-foreground text-lg mb-4">
              Select 1-3 Recognition Goals — the aspirational states the subject wants reflected back in their portraits.
            </p>
            <p className="text-primary text-sm">{selectedThemes.length} of 3 selected</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
            {packLoading || !genrePack ? (
              <div className="col-span-full text-center text-muted-foreground py-12">
                <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
                Loading Recognition Goals…
              </div>
            ) : (
              (genrePack.recognition_goals || []).map((entry) => {
                const theme = entry.goal;
                const isSelected = selectedThemes.includes(theme);
                return (
                  <motion.div
                    key={theme}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => toggleTheme(theme)}
                    className={`relative p-6 border rounded-sm cursor-pointer ${
                      isSelected ? "border-primary bg-primary/10" : "border-border hover:border-primary/30"
                    }`}
                    data-testid={`recognition-goal-${theme.toLowerCase().replace(/[/\s]/g, '-')}`}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <Check className="w-5 h-5 text-primary" />
                      </div>
                    )}
                    <h3 className="font-heading text-xl text-center mb-2">{theme}</h3>
                    {entry.underlying_recognition && (
                      <p className="text-xs text-muted-foreground text-center leading-snug">
                        “{entry.underlying_recognition}”
                      </p>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>

          <div className="max-w-md mx-auto mb-12">
            <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-4 text-center">
              Images Per Theme: {imagesPerTheme}
            </label>
            <input
              type="range"
              min="2"
              max="8"
              value={imagesPerTheme}
              onChange={(e) => setImagesPerTheme(parseInt(e.target.value))}
              className="w-full h-1 bg-border rounded-sm appearance-none cursor-pointer accent-primary"
              data-testid="images-per-theme-slider"
            />
          </div>

          <div className="max-w-md mx-auto">
            <button
              onClick={handleAnalyze}
              disabled={loading || selectedThemes.length < 1 || selectedThemes.length > 3 || isPending}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed py-4 px-8 uppercase tracking-widest text-xs font-bold rounded-sm flex items-center justify-center gap-2"
              data-testid="analyze-button"
              title={isPending ? "Account pending approval - analysis not available" : ""}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {isPending ? "Analysis Unavailable (Pending Approval)" : "Begin Analysis"}
                </>
              )}
            </button>

            {loading && progress && (
              <div className="mt-6 bg-card border border-border rounded-sm p-5" data-testid="analysis-progress">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Now analyzing</p>
                    <p className="font-heading text-xl text-primary">{progress.current_theme || '—'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Theme</p>
                    <p className="font-mono text-primary">
                      {Math.min((progress.themes_done || 0) + 1, progress.themes_total || 1)} / {progress.themes_total || 1}
                    </p>
                  </div>
                </div>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Images</span>
                  <span className="font-mono text-primary">
                    {progress.images_done || 0} / {progress.images_total || 0}
                  </span>
                </div>
                <div className="h-2 bg-muted/30 rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{
                      width: `${
                        progress.images_total
                          ? Math.min(100, ((progress.images_done || 0) / progress.images_total) * 100)
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  You can leave this page — analysis continues in the background. Check Recent Sessions to come back.
                </p>
              </div>
            )}

            {loading && !progress && (
              <p className="text-xs text-muted-foreground text-center mt-4" data-testid="analysis-warming-up">
                Warming up the AI… this can take up to a minute before progress appears.
              </p>
            )}

            {isPending && (
              <p className="text-warning text-sm text-center mt-2">
                Your account is pending approval. Please contact support to activate analysis features.
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Results Gallery (keep existing but add tile export and admin-only analysis)
const ResultsGallery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const sessionId = location.pathname.split("/").pop();
  
  const [results, setResults] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  // Per-theme expansion state for the "n images filtered out" disclosure.
  // Key: theme name → bool. We expand inline rather than open a modal so
  // photographers can compare skipped vs kept thumbnails side-by-side.
  const [expandedSkipped, setExpandedSkipped] = useState({});
  const [failedImages, setFailedImages] = useState(new Set()); // Track images that failed to load
  const tileRef = useRef(null);

  // Handler for image load errors - hides the tile
  const handleImageError = (imageKey) => {
    setFailedImages(prev => new Set([...prev, imageKey]));
  };

  useEffect(() => {
    fetchResults();
    fetchSession();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/results/${sessionId}`, { withCredentials: true });
      setResults(response.data.results);
    } catch (error) {
      console.error("Results Error:", error);
      toast.error("Failed to load results");
    } finally {
      setLoading(false);
    }
  };

  const fetchSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}`, { withCredentials: true });
      setSession(response.data);
    } catch (error) {
      console.error("Session Error:", error);
    }
  };

  const handleRefineAnalysis = () => {
    if (!session) {
      toast.error("Session data not loaded");
      return;
    }

    navigate("/theme-selection", {
      state: {
        profileId: session.client_profile_id,
        sessionData: {
          themes: session.selected_themes,
          imagesPerTheme: session.images_per_theme,
          clientProfile: session.client_profile
        }
      }
    });
  };

  const downloadImage = async (imageKey, imageName) => {
    try {
      const response = await axios.get(
        `${API}/download-image/${encodeURIComponent(imageKey)}?filename=${encodeURIComponent(imageName)}&session_id=${encodeURIComponent(sessionId)}`,
        { 
          responseType: 'blob',
          withCredentials: true 
        }
      );
      
      const blob = new Blob([response.data], { type: 'image/jpeg' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = imageName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("Image downloaded");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download image");
    }
  };

  const exportTileAsImage = async (image) => {
    try {
      console.log("Starting tile export for:", image.theme);
      toast.info("Generating tile image...");
      
      const payload = {
        image_key: image.image_key,
        theme: image.theme,
        category: image.category,
        ethos_score: image.ethos_score,
        pose_cue: image.pose_cue,
        emotional_prompt: image.emotional_prompt,
        micro_moment: image.micro_moment,
        analysis: image.analysis,
        client_name: session.client_name,
        is_admin: isAdmin,
        session_id: sessionId
      };
      
      console.log("Tile export payload:", payload);
      
      const response = await axios.post(`${API}/export-tile`, payload, {
        responseType: 'blob',
        withCredentials: true
      });
      
      console.log("Tile export response received, size:", response.data.size);
      
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const clientName = session?.client_name || 'Inspiration';
      link.download = `${clientName}_${image.theme}_tile.png`.replace(/\s+/g, '_');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      console.log("Download link clicked");
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("Tile image downloaded");
    } catch (error) {
      console.error("Export error details:", error.response?.data || error.message);
      toast.error(`Failed to export tile: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleExportAllImages = async () => {
    if (!results) return;
    
    try {
      toast.info("Creating ZIP file with all images...");
      
      const response = await axios.post(
        `${API}/export-images-zip/${sessionId}`,
        {},
        {
          responseType: 'blob',
          withCredentials: true
        }
      );
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${session?.client_name || 'Inspiration'}_Images.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("ZIP file downloaded!");
    } catch (error) {
      console.error("ZIP export error:", error);
      toast.error("Failed to create ZIP file");
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info("Generating PDF...");
      const response = await axios.post(`${API}/export-pdf/${sessionId}`, {}, {
        responseType: 'blob',
        withCredentials: true
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const clientName = session?.client_name || 'Inspiration';
      link.download = `${clientName}_Inspiration_Deck.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const isAdmin = user?.plan_type === 'admin';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="p-8"
        data-testid="results-gallery"
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-muted-foreground hover:text-primary"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={handleExportAllImages}
                className="flex items-center gap-2 bg-transparent border border-border text-foreground hover:bg-muted/20 px-4 py-2 rounded-sm uppercase tracking-widest text-xs"
              >
                <Download className="w-4 h-4" />
                Export Images
              </button>
              
              <button
                onClick={handleExportPDF}
                className="flex items-center gap-2 bg-transparent border border-border text-foreground hover:bg-muted/20 px-4 py-2 rounded-sm uppercase tracking-widest text-xs"
              >
                <FileText className="w-4 h-4" />
                Export PDF
              </button>

              <button
                onClick={handleRefineAnalysis}
                className="flex items-center gap-2 bg-transparent border border-primary/30 text-primary hover:bg-primary/10 px-6 py-2 rounded-sm uppercase tracking-widest text-xs"
              >
                <RefreshCw className="w-4 h-4" />
                Refine Analysis
              </button>
            </div>
          </div>

          <div className="mb-12">
            <h1 className="font-heading text-5xl lg:text-6xl tracking-tight mb-4">Curated Collection</h1>
            {session?.client_profile && (
              <div className="mb-4">
                <p className="text-primary text-lg">{session.client_profile.client_name}</p>
                <p className="text-muted-foreground text-sm">
                  {session.client_profile.personality_type} • Comfort Level {session.client_profile.comfort_level}/6
                </p>
              </div>
            )}
            <p className="text-muted-foreground text-lg">Your personalized inspiration deck</p>
          </div>

          {results?.map((themeResult) => (
            <div key={themeResult.theme} className="mb-16">
              <div className="sticky top-0 bg-background/80 backdrop-blur-sm py-4 mb-8 z-10">
                <h2 className="font-heading text-4xl text-primary">{themeResult.theme}</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {themeResult.images
                  .filter(img => !failedImages.has(img.image_key)) // Hide failed images
                  .map((img, idx) => (
                  <motion.div
                    key={img.image_key || idx}
                    whileHover={{ scale: 1.03 }}
                    className="image-card bg-card border border-border rounded-sm overflow-hidden group"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden cursor-pointer" onClick={() => setSelectedImage(img)}>
                      <img
                        src={img.thumb_url_600 || img.url}
                        srcSet={img.thumb_url_600 && img.thumb_url_1200 ? `${img.thumb_url_600} 600w, ${img.thumb_url_1200} 1200w` : undefined}
                        sizes="(max-width: 768px) 50vw, (max-width: 1280px) 33vw, 25vw"
                        loading="lazy"
                        decoding="async"
                        alt={`${themeResult.theme} inspiration`}
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(img.image_key)}
                      />
                      {/* Web image badge */}
                      {img.is_web_result && (
                        <span className="absolute top-2 left-2 px-2 py-1 bg-blue-500/90 text-white text-xs rounded-sm">
                          Web
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (img.is_web_result && img.web_url) {
                            // For web images, open in new tab
                            window.open(img.web_url, '_blank');
                          } else {
                            const clientName = session?.client_name || 'Inspiration';
                            downloadImage(img.image_key, `${clientName}_${themeResult.theme}_${idx + 1}.jpg`);
                          }
                        }}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-2 bg-black/80 hover:bg-primary rounded-sm"
                        title={img.is_web_result ? "Open in new tab" : "Download"}
                      >
                        <Download className="w-4 h-4 text-white" />
                      </button>
                    </div>
                    <div className="p-4 bg-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs uppercase tracking-widest text-primary">{img.category}</span>
                        <span className="text-xs text-foreground">Score: {img.ethos_score}/10</span>
                      </div>
                      {isAdmin && (
                        <p className="text-sm text-foreground/90 line-clamp-2">{img.analysis}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* "Why was my image skipped" disclosure — only renders when this
                  theme actually had images filtered out during analysis. */}
              {(() => {
                const skipped = themeResult.skipped_images || [];
                if (skipped.length === 0) return null;
                const isOpen = !!expandedSkipped[themeResult.theme];
                const REASON_LABEL = {
                  comfort_filter: "Outside comfort level",
                  gender_mismatch: "Wrong gender for this session",
                  analysis_error: "Analysis error",
                };
                return (
                  <div className="mt-6 border-t border-border/40 pt-4" data-testid={`skipped-disclosure-${themeResult.theme}`}>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedSkipped((prev) => ({
                          ...prev,
                          [themeResult.theme]: !prev[themeResult.theme],
                        }))
                      }
                      className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition"
                      data-testid={`skipped-toggle-${themeResult.theme}`}
                    >
                      {skipped.length} {skipped.length === 1 ? "image" : "images"} filtered out
                      <span className="ml-2 opacity-60">{isOpen ? "▲ hide" : "▼ see why"}</span>
                    </button>

                    {isOpen && (
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {skipped.map((sk, sidx) => (
                          <div
                            key={sk.image_key || sidx}
                            className="relative aspect-square rounded-sm overflow-hidden border border-border opacity-70"
                            data-testid={`skipped-tile-${themeResult.theme}-${sidx}`}
                          >
                            <img
                              src={sk.thumb_url_600 || sk.url}
                              alt=""
                              loading="lazy"
                              className="w-full h-full object-cover grayscale"
                            />
                            <div className="absolute inset-x-0 bottom-0 bg-black/80 px-2 py-1">
                              <div className="text-[10px] uppercase tracking-widest text-primary leading-tight">
                                {REASON_LABEL[sk.reason] || sk.reason}
                              </div>
                              {sk.note && (
                                <div className="text-[10px] text-foreground/80 mt-0.5 line-clamp-2 leading-tight">
                                  {sk.note}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>

        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-8"
              onClick={() => setSelectedImage(null)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="w-[90vw] max-w-[1400px] grid grid-cols-[2fr,1fr] gap-8"
                onClick={(e) => e.stopPropagation()}
                ref={tileRef}
              >
                <div className="rounded-sm overflow-hidden flex items-center justify-center bg-black">
                  <img
                    src={selectedImage.url}
                    alt="Selected"
                    className="w-full h-auto max-h-[85vh] object-contain"
                  />
                </div>
                <div className="text-left space-y-6 overflow-y-auto max-h-[85vh] bg-white/95 p-8 rounded-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-primary mb-2">{selectedImage.category}</p>
                      <h3 className="font-heading text-3xl mb-2 text-gray-900">{selectedImage.theme}</h3>
                      <p className="text-sm text-gray-600">Ethos Score: {selectedImage.ethos_score}/10</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => exportTileAsImage(selectedImage)}
                        className="flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-sm text-xs uppercase tracking-widest"
                      >
                        <Download className="w-4 h-4" />
                        Export Tile
                      </button>
                      <button
                        onClick={() => setSelectedImage(null)}
                        className="p-2 hover:bg-gray-200 rounded-sm"
                      >
                        <X className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm uppercase tracking-widest text-gray-500 mb-3">Pose Cue</h4>
                    <p className="text-gray-900 text-base leading-relaxed">{selectedImage.pose_cue}</p>
                  </div>

                  <div>
                    <h4 className="text-sm uppercase tracking-widest text-gray-500 mb-3">Emotional Activation</h4>
                    <p className="text-gray-900 text-base leading-relaxed">{selectedImage.emotional_prompt}</p>
                  </div>

                  <div>
                    <h4 className="text-sm uppercase tracking-widest text-gray-500 mb-3">Micro-Moment Direction</h4>
                    <p className="text-gray-900 text-base leading-relaxed">{selectedImage.micro_moment}</p>
                  </div>

                  {isAdmin && (
                    <div>
                      <h4 className="text-sm uppercase tracking-widest text-gray-500 mb-3">Analysis</h4>
                      <p className="text-gray-900 text-base leading-relaxed">{selectedImage.analysis}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

// Main App Router
function AppRouter() {
  const location = useLocation();

  // Check for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Fallback shown while a lazy-loaded route chunk is being fetched.
  const RouteFallback = (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-12 h-12 animate-spin text-primary" />
    </div>
  );

  return (
    <Suspense fallback={RouteFallback}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/portrait-plan" element={<ProtectedRoute><PortraitPlan /></ProtectedRoute>} />
        <Route path="/image-fit" element={<ProtectedRoute><ImageFit /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
        <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
        <Route path="/client-profile" element={<ProtectedRoute><ClientProfile /></ProtectedRoute>} />
        <Route path="/theme-selection" element={<ProtectedRoute><ThemeSelection /></ProtectedRoute>} />
        <Route path="/results/:sessionId" element={<ProtectedRoute><ResultsGallery /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" theme="dark" />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
